import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Database, Play, RefreshCw } from "lucide-react";
import {
  getNarrativeCacheStats,
  seedNarrativeCache,
} from "@/lib/bousala/narrative-seed.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

export const Route = createFileRoute("/_authenticated/admin/bousala/narrative-cache")({
  head: () => ({ meta: [{ title: "ذاكرة فقرات التقرير — بوصلة" }, { name: "robots", content: "noindex" }] }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: NarrativeCachePage,
});

type SeedResult = Awaited<ReturnType<typeof seedNarrativeCache>>;

const SLOT_LABEL: Record<string, string> = {
  fingerprint_desc: "وصف البصمة",
  personality_paragraph: "فقرة الشخصية",
  skills_paragraph: "فقرة المهارات",
  majors_intro: "مقدمة التخصصات",
};

function NarrativeCachePage() {
  const [result, setResult] = useState<SeedResult | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const fnSeed = useServerFn(seedNarrativeCache);

  const stats = useQuery({
    queryKey: ["admin", "bousala", "narrative-cache", "stats"],
    queryFn: () => getNarrativeCacheStats(),
  });

  const seed = useMutation({
    mutationFn: (slot: string) => fnSeed({ data: { slot: slot as never, limit: 25 } }),
    onSuccess: (data) => {
      setResult(data);
      toast.success(`تمت إضافة ${data.inserted} فقرة`);
      stats.refetch();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "فشل التوليد"),
    onSettled: () => setRunning(null),
  });

  return (
    <div className="space-y-4" dir="rtl">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="size-4" aria-hidden />
            ذاكرة فقرات التقرير
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => stats.refetch()} disabled={stats.isFetching}>
            <RefreshCw className={`size-4 ${stats.isFetching ? "animate-spin" : ""}`} aria-hidden />
            تحديث
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            توليد الفقرات المخزّنة مسبقاً حسب المفاتيح المستخرجة من المحاولات المكتملة. كل ضغطة تولّد 25 مفتاحاً كحد أقصى.
          </p>

          {stats.isLoading ? (
            <div className="h-24 animate-pulse rounded-md bg-muted" />
          ) : stats.data ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-start text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    <th className="p-2 text-start">الفقرة</th>
                    <th className="p-2 text-start">المفاتيح</th>
                    <th className="p-2 text-start">مُولَّدة</th>
                    <th className="p-2 text-start">متبقية</th>
                    <th className="p-2 text-start" />
                  </tr>
                </thead>
                <tbody>
                  {stats.data.slots.map((s) => (
                    <tr key={s.slot} className="border-b last:border-0">
                      <td className="p-2 font-medium">{SLOT_LABEL[s.slot] ?? s.slot}</td>
                      <td className="p-2 tabular-nums">{s.total}</td>
                      <td className="p-2 tabular-nums">{s.seeded}</td>
                      <td className="p-2 tabular-nums">{s.remaining}</td>
                      <td className="p-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={seed.isPending || s.remaining === 0}
                          onClick={() => {
                            setRunning(s.slot);
                            seed.mutate(s.slot);
                          }}
                        >
                          <Play className="size-4" aria-hidden />
                          {running === s.slot && seed.isPending ? "جارٍ التوليد…" : "توليد 25"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {result ? (
            <div className="rounded-lg bg-muted/50 p-3 text-sm">
              <div className="font-medium">{SLOT_LABEL[result.slot] ?? result.slot}</div>
              <div className="text-muted-foreground">
                جرت محاولة {result.attempted} — أُضيفت {result.inserted}، فشلت {result.failed}، المتبقي {result.remaining}
                {result.stoppedEarly ? " — توقّف مبكراً بسبب حدود المزوّد" : ""}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
